import { useCallback, useState } from 'react';

import { useTranslation } from '@/i18n';
import { monthlyCutsRexSyncService, mapMonthlyCutsRexSyncSummary } from '@/api/domains/attendance';
import type { MonthlyCutsRexSyncSummary } from '@/api/domains/attendance';
import { useUI } from '@/hooks/ui/useUI';
import { useAbortController } from '@/hooks/ui/useAbortController';


export interface UseSyncMonthlyCutsRexResult {
  syncCuts: (monthlyCutIds: number[]) => Promise<MonthlyCutsRexSyncSummary | null>;
  isSyncing: boolean;
}

// Sincroniza uno o varios cortes mensuales con rex+ remuneraciones (ASIS-4945). Informa el
// resultado por toast: éxito cuando todos los cortes solicitados quedaron sincronizados, y
// advertencia con el resumen cuando hubo cortes ya sincronizados por otro usuario, con error de
// rex+ o de empresas sin integración. El refresco del listado y la limpieza de la selección quedan
// en el llamador.
export function useSyncMonthlyCutsRex(): UseSyncMonthlyCutsRexResult {
  const { t: translate } = useTranslation();
  const { showApiError, showError, showSuccess, showWarning } = useUI();
  const { createSignal } = useAbortController();

  const [isSyncing, setIsSyncing] = useState(false);

  const syncCuts = useCallback(
    async (monthlyCutIds: number[]): Promise<MonthlyCutsRexSyncSummary | null> => {
      if (monthlyCutIds.length === 0) return null;

      setIsSyncing(true);
      try {
        const response = await monthlyCutsRexSyncService.sync({ monthlyCutIds }, createSignal());

        if (response.error?.code === 'ABORTED') return null;
        if (!response.success || !response.data) {
          showApiError(
            response,
            translate('attendance:monthlyCutsList.payrollSync.messages.syncError'),
          );
          return null;
        }

        const summary = mapMonthlyCutsRexSyncSummary(response.data);
        // Cortes omitidos por no ser aplicables: empresa sin integración rex+ o corte inexistente /
        // de otro holding. Se cuentan aparte de los ya sincronizados por otro usuario, porque el
        // motivo es distinto y confundirlos daría un mensaje falso.
        const skippedCount = summary.notApplicableIds.length + summary.notFoundIds.length;
        const hasIssues =
          summary.alreadySyncedIds.length > 0 || summary.failedIds.length > 0 || skippedCount > 0;

        if (summary.syncedItems.length === 0 && summary.failedIds.length > 0) {
          // Nada se sincronizó y rex+ falló: el usuario necesita un mensaje de error claro, no un
          // resumen con contadores en cero.
          showError(translate('attendance:monthlyCutsList.payrollSync.messages.syncError'));
        } else if (!hasIssues) {
          showSuccess(
            translate('attendance:monthlyCutsList.payrollSync.messages.syncSuccess', {
              count: summary.syncedItems.length,
            }),
          );
        } else {
          const partialMessage = translate(
            'attendance:monthlyCutsList.payrollSync.messages.syncPartial',
            {
              synced: summary.syncedItems.length,
              alreadySynced: summary.alreadySyncedIds.length,
              failed: summary.failedIds.length,
            },
          );
          const skippedNote = skippedCount > 0
            ? ` ${translate('attendance:monthlyCutsList.payrollSync.messages.syncSkippedNote', {
              skipped: skippedCount,
            })}`
            : '';

          showWarning(`${partialMessage}${skippedNote}`);
        }

        return summary;
      } finally {
        setIsSyncing(false);
      }
    },
    [createSignal, showApiError, showError, showSuccess, showWarning, translate],
  );

  return { syncCuts, isSyncing };
}

export default useSyncMonthlyCutsRex;
